import { Zip, ZipPassThrough, unzipSync } from 'fflate';

const MONTHS_ES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
];

// Sleep/now indirection so tests can bypass real timers without global mocks.
export const _internals = {
  sleep: (ms: number) => new Promise<void>((r) => setTimeout(r, ms)),
  now: () => Date.now(),
};

export interface MonthlyZipResult {
  month: number;
  monthLabel: string;
  blob: Blob | null;
  filename: string;
  fileCount: number;
}

export interface FetchMonthlyZipOpts {
  url: string;
  contractorUserId: string;
  year: number;
  month: number;
  accessToken: string;
  signal: AbortSignal;
  maxWaitMs?: number;
}

async function fetchWithRetry(input: RequestInfo, init: RequestInit, retries = 3): Promise<Response> {
  let lastErr: unknown = null;
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      const res = await fetch(input, init);
      const retryable = res.status === 429 || res.status >= 500;
      if (retryable && attempt < retries) {
        await _internals.sleep((attempt + 1) * 1500);
        continue;
      }
      return res;
    } catch (err) {
      lastErr = err;
      if ((init.signal as AbortSignal | undefined)?.aborted) throw err;
      if (attempt < retries) {
        await _internals.sleep((attempt + 1) * 1500);
        continue;
      }
    }
  }
  throw lastErr;
}

export async function fetchMonthlyZip(opts: FetchMonthlyZipOpts): Promise<MonthlyZipResult> {
  const { url, contractorUserId, year, month, accessToken, signal } = opts;
  const maxWaitMs = opts.maxWaitMs ?? 900_000;
  const monthLabel = MONTHS_ES[month - 1];

  const res = await fetchWithRetry(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      contractor_user_id: contractorUserId,
      year,
      month,
      access_token: accessToken,
    }),
    signal,
  });

  let downloadUrl: string | null = null;
  let filename = `${monthLabel}${year}.zip`;
  let fileCount = 0;

  if (res.status === 202) {
    const { code: activationId } = await res.json();
    if (!activationId) throw new Error(`DO 202 sin activation id (mes ${month})`);

    const intervalMs = 5000;
    const start = _internals.now();
    while (_internals.now() - start < maxWaitMs) {
      if (signal.aborted) throw new DOMException('Aborted', 'AbortError');
      await _internals.sleep(intervalMs);
      const pollRes = await fetchWithRetry(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          contractor_user_id: contractorUserId,
          activation_id: activationId,
          access_token: accessToken,
        }),
        signal,
      });
      if (!pollRes.ok) continue;
      const pollBody = await pollRes.json();
      if (pollBody.ready && pollBody.download_url) {
        downloadUrl = pollBody.download_url;
        break;
      }
    }
    if (!downloadUrl) throw new Error(`ZIP mes ${month} no estuvo listo en 15 minutos`);
    fileCount = -1;
  } else if (res.ok) {
    const body = await res.json();
    fileCount = body.file_count ?? 0;
    if (fileCount === 0) {
      return { month, monthLabel, blob: null, filename: body.filename ?? filename, fileCount: 0 };
    }
    downloadUrl = body.download_url;
    filename = body.filename ?? filename;
  } else {
    throw new Error(await res.text());
  }

  const blobRes = await fetchWithRetry(downloadUrl!, { signal });
  if (!blobRes.ok) throw new Error(`Descarga ZIP mes ${month} falló: ${blobRes.status}`);
  const blob = await blobRes.blob();
  return { month, monthLabel, blob, filename, fileCount: fileCount === -1 ? Math.max(1, fileCount) : fileCount };
}

export interface RunAnnualFanOutOpts {
  url: string;
  contractorUserId: string;
  year: number;
  accessToken: string;
  signal: AbortSignal;
  concurrency?: number;
  onProgress?: (done: number, total: number) => void;
  fetchMonthly?: typeof fetchMonthlyZip;
}

export interface AnnualFanOutResult {
  results: (MonthlyZipResult | null)[];
  failed: number[];
}

export async function runAnnualFanOut(opts: RunAnnualFanOutOpts): Promise<AnnualFanOutResult> {
  const {
    url, contractorUserId, year, accessToken, signal,
    concurrency = 4, onProgress,
  } = opts;
  const fetchMonthly = opts.fetchMonthly ?? fetchMonthlyZip;

  const months = Array.from({ length: 12 }, (_, i) => i + 1);
  const results: (MonthlyZipResult | null)[] = new Array(12).fill(null);
  const failed: number[] = [];
  let cursor = 0;
  let done = 0;

  const tryFetch = async (month: number): Promise<MonthlyZipResult> => {
    return fetchMonthly({
      url, contractorUserId, year, month, accessToken, signal,
    });
  };

  const worker = async () => {
    while (cursor < months.length) {
      const idx = cursor++;
      const month = months[idx];
      try {
        results[idx] = await tryFetch(month);
      } catch (err: any) {
        if (err?.name === 'AbortError') throw err;
        failed.push(month);
      }
      done++;
      onProgress?.(done, 12);
    }
  };

  await Promise.all(Array.from({ length: concurrency }, worker));

  // Dos retry passes secuenciales con delays crecientes.
  for (const pass of [1, 2]) {
    if (failed.length === 0) break;
    const retryQueue = [...failed];
    failed.length = 0;
    for (const month of retryQueue) {
      if (signal.aborted) throw new DOMException('Aborted', 'AbortError');
      try {
        results[month - 1] = await tryFetch(month);
      } catch (err: any) {
        if (err?.name === 'AbortError') throw err;
        failed.push(month);
      }
      onProgress?.(12 - failed.length, 12);
      await _internals.sleep(pass * 2000);
    }
  }

  return { results, failed };
}

export interface AssembleAnnualOpts {
  outputName: string;
  onPhaseChange?: (phase: 'assembling') => void;
}

export async function assembleAnnualZip(
  monthlyResults: MonthlyZipResult[],
  opts: AssembleAnnualOpts,
): Promise<void> {
  opts.onPhaseChange?.('assembling');

  let writer: { write(chunk: Uint8Array): Promise<void>; close(): Promise<void> } | null = null;
  let chunks: Uint8Array[] | null = null;

  const fsaSupported = typeof (window as any).showSaveFilePicker === 'function';
  if (fsaSupported) {
    try {
      const handle = await (window as any).showSaveFilePicker({
        suggestedName: opts.outputName,
        types: [{ description: 'ZIP', accept: { 'application/zip': ['.zip'] } }],
      });
      writer = await handle.createWritable();
    } catch (err: any) {
      if (err?.name === 'AbortError') throw err;
      chunks = [];
    }
  } else {
    chunks = [];
  }

  await new Promise<void>((resolve, reject) => {
    const zip = new Zip((err, chunk, final) => {
      if (err) { reject(err); return; }
      if (writer) {
        writer.write(chunk).then(() => {
          if (final) writer!.close().then(() => resolve()).catch(reject);
        }).catch(reject);
      } else {
        chunks!.push(chunk);
        if (final) {
          const blob = new Blob(chunks as BlobPart[], { type: 'application/zip' });
          chunks = null;
          const a = document.createElement('a');
          a.href = URL.createObjectURL(blob);
          a.download = opts.outputName;
          document.body.appendChild(a);
          a.click();
          a.remove();
          setTimeout(() => URL.revokeObjectURL(a.href), 5000);
          resolve();
        }
      }
    });

    (async () => {
      try {
        for (const r of monthlyResults) {
          if (!r || !r.blob || r.fileCount <= 0) continue;
          const monthBuf = new Uint8Array(await r.blob.arrayBuffer());
          const entries = unzipSync(monthBuf);
          const prefix = `${String(r.month).padStart(2, '0')}_${r.monthLabel}/`;
          for (const [name, data] of Object.entries(entries)) {
            const zpt = new ZipPassThrough(prefix + name);
            zip.add(zpt);
            zpt.push(data, true);
          }
        }
        zip.end();
      } catch (err) {
        reject(err);
      }
    })();
  });
}
